import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Filter } from "lucide-react";
import { createTenant, fetchTenants, fetchPackages, listReferrers, fetchAiUsageAll } from "../services/api";
import type { PackOption } from "../services/api";
import type { Tenant, TenantStatus } from "../types";
import type { ReferrerPartner } from "../types/referrals";
import { usePacks, PACK_GROUP_LABELS, PACK_GROUP_ORDER, formatPerRep, formatPackPrice, isSellable, packIncludes, packExcludes, type PackGroup } from "../services/packs";
import { StatusPill } from "../components/StatusPill";
import { PackTag } from "../components/PackTag";
import { useToast } from "../components/ToastProvider";
import { usePageTitle } from "../hooks/usePageTitle";

const STATUS_FILTERS: { label: string; value: TenantStatus | "all" }[] = [
  { label: "All", value: "all" },
  { label: "Active", value: "active" },
  { label: "Awaiting setup", value: "awaiting_setup" },
  { label: "Ready to launch", value: "ready_for_production" },
  { label: "Suspended", value: "suspended" }
];

export function CompaniesPage() {
  usePageTitle("Companies");
  const navigate = useNavigate();
  const toast = useToast();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<TenantStatus | "all">("all");
  const [group, setGroup] = useState<PackGroup | "all">("all");
  const [showNew, setShowNew] = useState(false);
  const [aiUsd, setAiUsd] = useState<Record<string, { costUsd: number; turns: number }>>({});
  const { bySlug } = usePacks();

  useEffect(() => {
    let active = true;
    fetchTenants()
      .then((t) => { if (active) setTenants(t); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  // Per-tenant Assist spend this month (best-effort; blank if the orchestrator
  // is unreachable). Keyed by tenant id.
  useEffect(() => {
    let active = true;
    fetchAiUsageAll()
      .then((r) => {
        if (!active) return;
        const m: Record<string, { costUsd: number; turns: number }> = {};
        for (const row of r.tenants) m[row.tenantId] = { costUsd: row.costUsd, turns: row.turns };
        setAiUsd(m);
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tenants.filter((t) => {
      if (status !== "all" && t.status !== status) return false;
      if (group !== "all" && (t.pack ? bySlug[t.pack]?.group : undefined) !== group) return false;
      if (!q) return true;
      const packLabel = t.pack ? bySlug[t.pack]?.label ?? "" : "";
      return (
        t.name.toLowerCase().includes(q) ||
        t.slug.toLowerCase().includes(q) ||
        t.primaryContactEmail.toLowerCase().includes(q) ||
        t.industry.toLowerCase().includes(q) ||
        packLabel.toLowerCase().includes(q) ||
        t.tags.some((tag) => tag.toLowerCase().includes(q))
      );
    });
  }, [tenants, query, status, group, bySlug]);

  async function onCreated(
    t: Tenant & { ownerInvited?: boolean; ownerInviteEmailSent?: boolean; packApplied?: boolean }
  ) {
    // Say the worst thing first. A company with no feature pack is an empty
    // product — the owner logs in and finds nothing — and it used to be
    // reported only in the audit log, so the operator moved on and the
    // customer discovered it. This outranks any email problem.
    if (t.packApplied === false) {
      toast.push(
        "error",
        `Created ${t.name}, but the ${t.pack ?? ""} package did NOT apply — they have no features yet. ` +
          `Re-apply it from Remote Config before handing this company over.`
      );
    } else if (t.ownerInviteEmailSent) {
      toast.push("success", `Created ${t.name} and emailed the owner a setup link. Next: provision Twilio.`);
    } else if (t.ownerInvited) {
      toast.push("error", `Created ${t.name}, but the owner setup email didn't send — use Resend invite on the company page.`);
    } else {
      toast.push("success", `Created ${t.name}. Next: provision Twilio and invite the owner.`);
    }
    setTenants((prev) => [t, ...prev]);
    setShowNew(false);
    navigate(`/companies/${t.id}`);
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Customers</span>
          <h1>Companies</h1>
          <p className="muted">Every business running on the Orbit platform.</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => setShowNew(true)}>
            <Plus size={14} /> New company
          </button>
        </div>
      </header>

      <div className="card" style={{ padding: 16 }}>
        <div className="row wrap" style={{ gap: 12 }}>
          <div className="search-bar" style={{ flex: "1 1 280px" }}>
            <Search size={14} />
            <input
              type="search"
              placeholder="Search by name, slug, contact, industry, tag…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="row" style={{ gap: 8 }}>
            <Filter size={14} style={{ color: "var(--text-mute)" }} />
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                className={`tab ${status === f.value ? "active" : ""}`}
                onClick={() => setStatus(f.value)}
                style={{ background: "transparent", border: "1px solid var(--border)" }}
              >
                {f.label}
              </button>
            ))}
          </div>
          <select value={group} onChange={(e) => setGroup(e.target.value as PackGroup | "all")} style={{ maxWidth: 180 }}>
            <option value="all">All packages</option>
            {PACK_GROUP_ORDER.map((g) => (
              <option key={g} value={g}>{PACK_GROUP_LABELS[g]}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Industry</th>
              <th>Package</th>
              <th>Status</th>
              <th>Users</th>
              <th>Last active</th>
              <th className="right">AI (mo)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8}>
                  <div className="empty-state">
                    <p className="muted">Loading companies…</p>
                  </div>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <div className="empty-state">
                    <h3>No companies match those filters</h3>
                    <p>Try clearing the search.</p>
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((t) => (
                <tr key={t.id} className="row-link">
                  <td>
                    <Link to={`/companies/${t.id}`} className="row-link-name">{t.name}</Link>
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      {t.primaryContactEmail} • {t.slug}
                    </div>
                  </td>
                  <td className="muted">{t.industry}</td>
                  <td><PackTag pack={t.pack} /></td>
                  <td><StatusPill status={t.status} /></td>
                  <td className="tnum">
                    {t.usage.users}
                  </td>
                  <td className="muted nowrap">{relativeDate(t.lastActiveAt)}</td>
                  <td className="tnum right" title={aiUsd[t.id] ? `${aiUsd[t.id].turns} chats this month` : "no Assist usage yet"}>
                    {aiUsd[t.id] ? `$${aiUsd[t.id].costUsd.toFixed(2)}` : <span className="muted">-</span>}
                  </td>
                  <td className="right muted" aria-hidden>→</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showNew && <NewCompanyModal onClose={() => setShowNew(false)} onCreated={onCreated} />}
    </div>
  );
}

function NewCompanyModal({ onClose, onCreated }: { onClose: () => void; onCreated: (t: Tenant & { ownerInvited?: boolean; ownerInviteEmailSent?: boolean; packApplied?: boolean }) => void }) {
  const [form, setForm] = useState({
    name: "",
    slug: "",
    industry: "Sales & services",
    country: "ZA",
    primaryContactEmail: "",
    ownerFullName: "",
    primaryContactPhone: "+27",
    pack: "",
    referrerId: "",
    notes: "",
    sendOwnerInvite: true,
    skipPayment: true,
    // Billing terms. "defer" is the default: the company goes live now and the
    // real numbers get attached later from the company page. Numeric fields are
    // held as strings so a half-typed value doesn't fight the input.
    billingMode: "defer",
    monthlyAmountRands: "",
    reps: "",
    billingAnchor: "day_25",
    freeMonths: "2",
    freeUntil: ""
  });
  const [packs, setPacks] = useState<PackOption[]>([]);
  const [referrers, setReferrers] = useState<ReferrerPartner[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function update<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  // Load the REAL package catalog + the referrer partners.
  useEffect(() => {
    let active = true;
    fetchPackages()
      .then((res) => {
        if (!active) return;
        setPacks(res.packs);
        setForm((prev) =>
          prev.pack
            ? prev
            : { ...prev, pack: res.packs.find((p) => p.pack === "comms-pro")?.pack || res.packs[0]?.pack || "" }
        );
      })
      .catch(() => undefined);
    listReferrers()
      .then((res) => { if (active) setReferrers((res.items ?? []).filter((r) => r.status !== "suspended")); })
      .catch(() => undefined);
    return () => { active = false; };
  }, []);

  // Auto-slug from the name until the operator hand-edits it in Advanced.
  useEffect(() => {
    if (slugEdited) return;
    const auto = form.name
      .toLowerCase().normalize("NFKD")
      .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
    update("slug", auto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name, slugEdited]);

  const selectedPack = packs.find((p) => p.pack === form.pack);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.pack) { setError("Pick a package."); return; }
    setBusy(true);
    setError(null);
    try {
      const t = await createTenant({
        name: form.name,
        slug: form.slug || undefined,
        industry: form.industry,
        country: form.country,
        primaryContactEmail: form.primaryContactEmail,
        primaryContactPhone: form.primaryContactPhone,
        pack: form.pack,
        referrerId: form.referrerId || undefined,
        notes: form.notes || undefined,
        ownerFullName: form.ownerFullName || undefined,
        sendOwnerInvite: form.sendOwnerInvite,
        skipPayment: form.skipPayment,
        // Rands in the UI, cents on the wire. Only send terms when we're the
        // ones managing billing — a self-service company still pays online.
        billing: form.skipPayment
          ? {
              mode: form.billingMode === "schedule" ? "schedule" : "defer",
              monthlyAmountCents: form.monthlyAmountRands.trim()
                ? Math.round(Number(form.monthlyAmountRands) * 100)
                : undefined,
              reps: form.reps.trim() ? Number(form.reps) : undefined,
              billingAnchor: form.billingAnchor === "last_day" ? "last_day" : "day_25",
              freeMonths: Number(form.freeMonths),
              freeUntil: form.freeUntil || undefined
            }
          : undefined
      });
      onCreated(t);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the company.");
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>New company</h2>
        <p>Emails the owner a set-password link automatically. With <strong>Skip online payment</strong> on (default) they set a password and go straight into the app — no payment wizard. You'll still need to provision Twilio.</p>
        <form className="form" onSubmit={submit}>
          <div className="form-grid">
            <label>
              Business name
              <input required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Naledi Motors" />
            </label>
            <label>
              Industry
              <input value={form.industry} onChange={(e) => update("industry", e.target.value)} />
            </label>
            <label>
              Owner email
              <input type="email" required value={form.primaryContactEmail} onChange={(e) => update("primaryContactEmail", e.target.value)} />
            </label>
            <label>
              Owner name <span className="muted">(optional)</span>
              <input value={form.ownerFullName} onChange={(e) => update("ownerFullName", e.target.value)} placeholder="Thabo Nkosi" />
            </label>
            <label>
              Owner phone
              <input value={form.primaryContactPhone} onChange={(e) => update("primaryContactPhone", e.target.value)} />
            </label>
            <label>
              Country
              <select value={form.country} onChange={(e) => update("country", e.target.value)}>
                <option value="ZA">South Africa</option>
                <option value="NA">Namibia</option>
                <option value="BW">Botswana</option>
                <option value="ZW">Zimbabwe</option>
              </select>
            </label>
            <label>
              Package
              <select required value={form.pack} onChange={(e) => update("pack", e.target.value)}>
                {packs.length === 0 ? <option value="">Loading…</option> : null}
                {/* Only the packages we still sell. The retired tiers stay out of
                    this list entirely - a brand new company should never land on
                    one, and they are still switchable from the company's own
                    Package tab if a migration ever needs it. */}
                {packs.filter(isSellable).map((p) => (
                  <option key={p.pack} value={p.pack}>
                    {p.label} — {formatPackPrice(p)}
                    {p.flatMonthlyCents != null
                      ? " · unlimited users"
                      : p.perRepMonthlyCents != null
                        ? " · no rep limit"
                        : " · quoted, unlimited users"}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {selectedPack ? (
            <p className="muted" style={{ fontSize: 12.5, marginTop: -4 }}>
              {selectedPack.description} <strong>{formatPackPrice(selectedPack)}</strong>
              {packIncludes(selectedPack).length ? (
                <>
                  <br />
                  <strong style={{ color: "var(--text)" }}>Unlocks:</strong> {packIncludes(selectedPack).join(" · ")}
                  {packExcludes(selectedPack).length ? (
                    <>
                      <br />
                      <span style={{ opacity: 0.8 }}>No: {packExcludes(selectedPack).join(" · ")}</span>
                    </>
                  ) : null}
                </>
              ) : null}
            </p>
          ) : null}

          <label>
            Referred by <span className="muted">(optional)</span>
            <select value={form.referrerId} onChange={(e) => update("referrerId", e.target.value)}>
              <option value="">— No referrer —</option>
              {referrers.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.fullName}{r.company ? ` · ${r.company}` : ""}{r.status === "invited" ? " (invited)" : ""}
                </option>
              ))}
            </select>
          </label>

          <label>
            Internal notes
            <textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Anything ops should know…" />
          </label>

          <label style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={form.sendOwnerInvite}
              onChange={(e) => update("sendOwnerInvite", e.target.checked)}
              style={{ width: "auto" }}
            />
            <span>
              Email the owner a set-password link now
              {form.primaryContactEmail ? <> (<strong>{form.primaryContactEmail}</strong>)</> : null}
            </span>
          </label>

          <label style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={form.skipPayment}
              onChange={(e) => update("skipPayment", e.target.checked)}
              style={{ width: "auto" }}
            />
            <span>
              Skip online payment — operator-managed billing
              <span className="muted" style={{ display: "block", fontSize: 11.5 }}>
                Owner just sets a password and goes straight into the app (no payment wizard, no email OTP).
                Untick only if this company should pay online itself.
              </span>
            </span>
          </label>

          {form.skipPayment ? (
            <fieldset style={{ border: "1px solid var(--border, #e2e2e2)", borderRadius: 8, padding: 12 }}>
              <legend style={{ fontSize: 12.5, padding: "0 6px" }}>Billing</legend>

              <label>
                When
                <select value={form.billingMode} onChange={(e) => update("billingMode", e.target.value)}>
                  <option value="defer">Set it up later — company goes live now</option>
                  <option value="schedule">Set the schedule now</option>
                </select>
                <span className="muted" style={{ fontSize: 11.5 }}>
                  {form.billingMode === "defer"
                    ? "Company is fully usable straight away and shows as awaiting billing on the board. Nothing is ever debited without a mandate, so this is safe to leave."
                    : "Free period and first debit date are set now, exactly like a company that signed up and paid online."}
                </span>
              </label>

              <div className="form-grid">
                <label>
                  Monthly amount <span className="muted">(R, optional)</span>
                  <input
                    inputMode="decimal"
                    value={form.monthlyAmountRands}
                    onChange={(e) => update("monthlyAmountRands", e.target.value)}
                    placeholder={
                      selectedPack?.perRepMonthlyCents
                        ? `list is ${(selectedPack.perRepMonthlyCents / 100).toFixed(0)} per rep`
                        : "custom quote"
                    }
                  />
                </label>
                <label>
                  Reps billed <span className="muted">(optional)</span>
                  <input
                    inputMode="numeric"
                    value={form.reps}
                    onChange={(e) => update("reps", e.target.value)}
                    placeholder="reps this is sold at"
                  />
                </label>
              </div>

              {form.billingMode === "schedule" ? (
                <div className="form-grid">
                  <label>
                    Debit day
                    <select value={form.billingAnchor} onChange={(e) => update("billingAnchor", e.target.value)}>
                      <option value="day_25">25th of the month</option>
                      <option value="last_day">Last day of the month</option>
                    </select>
                  </label>
                  <label>
                    Free months
                    <select
                      value={form.freeMonths}
                      onChange={(e) => update("freeMonths", e.target.value)}
                      disabled={!!form.freeUntil}
                    >
                      <option value="0">None — charged from the start</option>
                      <option value="1">1 month free</option>
                      <option value="2">2 months free</option>
                    </select>
                  </label>
                  <label>
                    Or free until <span className="muted">(overrides)</span>
                    <input
                      type="date"
                      value={form.freeUntil}
                      onChange={(e) => update("freeUntil", e.target.value)}
                    />
                    <span className="muted" style={{ fontSize: 11.5 }}>
                      For a bespoke deal. The first debit lands on the first {form.billingAnchor === "last_day" ? "month-end" : "25th"} after this date.
                    </span>
                  </label>
                </div>
              ) : null}
            </fieldset>
          ) : null}

          <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: "flex-start" }}
            onClick={() => setShowAdvanced((s) => !s)}>
            {showAdvanced ? "Hide advanced" : "Show advanced"}
          </button>
          {showAdvanced ? (
            <label>
              Slug
              <input
                value={form.slug}
                onChange={(e) => { setSlugEdited(true); update("slug", e.target.value); }}
                placeholder="naledi-motors"
              />
              <span className="muted" style={{ fontSize: 11.5 }}>
                The company's short ID used in links and as a unique key. Auto-filled from the name — you rarely need to touch this.
              </span>
            </label>
          ) : null}

          {error ? <p className="error">{error}</p> : null}

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? "Creating…" : "Create company"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function relativeDate(iso: string | null): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  const d = Math.floor(ms / 86400000);
  if (d === 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d} days ago`;
  if (d < 365) return `${Math.floor(d / 30)} mo ago`;
  return `${Math.floor(d / 365)} yr ago`;
}
